import { H5 } from 'foundations-components/transitional/components/H5';
import { getPublicLang } from 'academy-common-lib/utils/url';
import { Tag } from 'foundations-components/transitional/components/Tag';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ACContentBreakdownList from 'academy-common-lib/components/list/ACContentBreakdownList';
import { useEffect, useRef, useState } from 'react';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { View } from 'foundations-components/components/View';
import { getLanguageUrl } from '../../../utils/urls';
import { getAvailableLanguages } from '../../../utils/linkedLanguages';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LIST_MARGIN = '1000';
const LIST_PADDING = '600';
const DynamicWidthSection = ({
  children,
  embeddedSignupRef,
  marginBlockEnd
}) => {
  const ref = useRef(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  useEffect(() => {
    if (!ref.current || !(embeddedSignupRef !== null && embeddedSignupRef !== void 0 && embeddedSignupRef.current)) return;
    const observer = new ResizeObserver(() => {
      if (!ref.current || !(embeddedSignupRef !== null && embeddedSignupRef !== void 0 && embeddedSignupRef.current)) return;
      const elementTop = ref.current.getBoundingClientRect().top;
      const signupBottom = embeddedSignupRef.current.getBoundingClientRect().bottom;
      setIsIntersecting(elementTop < signupBottom);
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [embeddedSignupRef]);
  const content = /*#__PURE__*/_jsx(View, {
    inlineSize: isIntersecting ? '50%' : '100%',
    paddingInlineEnd: isIntersecting ? '600' : '0',
    ref: ref,
    children: children
  });
  return marginBlockEnd ? /*#__PURE__*/_jsx(View, {
    marginBlockEnd: marginBlockEnd,
    children: content
  }) : content;
};
const PublicLandingPageSecondaryDetails = ({
  description,
  personas,
  learnings,
  availableLanguages = [getPublicLang()],
  languageLinkedContent,
  contentType,
  pagePath,
  duration,
  lessonCount,
  quizCount,
  videoCount,
  exerciseCount,
  subCategories,
  embeddedSignupRef
}) => {
  const currentLang = getPublicLang();
  const derivedAvailableLanguages = getAvailableLanguages({
    languageLinkedContent,
    legacyLanguages: availableLanguages,
    currentLanguage: currentLang
  });
  const otherLanguages = derivedAvailableLanguages.filter(lang => lang !== currentLang).sort();
  return /*#__PURE__*/_jsxs(View, {
    as: "section",
    "data-test-id": "public-landing-page-secondary-details",
    inlineSize: "100%",
    minBlockSize: "400px",
    paddingBlock: "300",
    children: [personas && !!personas.length && /*#__PURE__*/_jsxs(DynamicWidthSection, {
      embeddedSignupRef: embeddedSignupRef,
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.contentLandingPage.secondaryDetails.personasHeading"
        })
      }), /*#__PURE__*/_jsx(List, {
        marginBlockEnd: LIST_MARGIN,
        paddingInlineStart: LIST_PADDING,
        styled: true,
        wrapChildren: true,
        children: personas
      })]
    }), learnings && !!learnings.length && /*#__PURE__*/_jsxs(DynamicWidthSection, {
      embeddedSignupRef: embeddedSignupRef,
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.contentLandingPage.secondaryDetails.learningsHeading"
        })
      }), /*#__PURE__*/_jsx(List, {
        marginBlockEnd: LIST_MARGIN,
        paddingInlineStart: LIST_PADDING,
        styled: true,
        wrapChildren: true,
        children: learnings
      })]
    }), otherLanguages && !!otherLanguages.length && /*#__PURE__*/_jsxs(DynamicWidthSection, {
      embeddedSignupRef: embeddedSignupRef,
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.contentLandingPage.secondaryDetails.availableLanguagesHeading"
        })
      }), /*#__PURE__*/_jsx(List, {
        inline: true,
        marginBlockEnd: LIST_MARGIN,
        children: otherLanguages.map(lang => /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Tag, {
            href: getLanguageUrl({
              language: lang,
              contentType,
              pagePath,
              languageLinkedContent
            }),
            children: LanguageNames[lang]
          })
        }, lang))
      })]
    }), /*#__PURE__*/_jsxs(DynamicWidthSection, {
      embeddedSignupRef: embeddedSignupRef,
      marginBlockEnd: LIST_MARGIN,
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.contentLandingPage.secondaryDetails.includesHeading"
        })
      }), /*#__PURE__*/_jsx(ACContentBreakdownList, {
        certificateCount: contentType === 'CERTIFICATION' ? 1 : 0,
        duration: duration,
        exerciseCount: exerciseCount,
        lessonCount: lessonCount,
        quizCount: quizCount,
        videoCount: videoCount
      })]
    }), subCategories && !!subCategories.length && /*#__PURE__*/_jsx(DynamicWidthSection, {
      embeddedSignupRef: embeddedSignupRef,
      children: /*#__PURE__*/_jsx(List, {
        inline: true,
        marginBlockEnd: LIST_MARGIN,
        styled: true,
        wrapChildren: true,
        children: subCategories.map(subCategory => /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Tag, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `academyPublic.categories.${subCategory}`
            })
          })
        }, subCategory))
      })
    }), description && /*#__PURE__*/_jsxs(DynamicWidthSection, {
      embeddedSignupRef: embeddedSignupRef,
      marginBlockEnd: LIST_MARGIN,
      children: [/*#__PURE__*/_jsx(H5, {
        children: contentType === 'LESSON' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.contentLandingPage.secondaryDetails.lessonDescription"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.contentLandingPage.secondaryDetails.courseDescription"
        })
      }), description]
    })]
  });
};
export default PublicLandingPageSecondaryDetails;